import React, { useState, useEffect, useRef } from "react";
import "../styles/bookingmodal.css";
import "../styles/booking-date-picker.css";
import guestHouseService from "../services/guestHouseService";
import bookingService from "../services/bookingService";
import fileService from "../services/fileService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import BookingSuccessModal from "../components/BookingSuccessModal";
import { debounce } from "lodash";
import authService from "../services/authService";

const BookingComponent = ({ isOpen, onClose, editMode = false, bookingData = null }) => {
  // Guest search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const searchInputRef = useRef(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Form fields state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [cpfNo, setCpfNo] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [selectedGuestHouse, setSelectedGuestHouse] = useState("");
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [rooms, setRooms] = useState("");
  const [adult, setAdult] = useState("");
  const [child, setChild] = useState("");
  const [guestType, setGuestType] = useState("");
  const [otherGuestType, setOtherGuestType] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [travelType, setTravelType] = useState("");
  const [finalRoomPrice, setFinalRoomPrice] = useState();

  // Suite room state variables
  const [hasSuiteRooms, setHasSuiteRooms] = useState(false);
  const [suiteRoomsData, setSuiteRoomsData] = useState([]);
  const [selectedSuiteRoom, setSelectedSuiteRoom] = useState("");
  const [suiteRoomPrice, setSuiteRoomPrice] = useState("0");

  // Form validation state
  const [errors, setErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Other state variables
  const [guestHouses, setGuestHouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGuestHouseDetails, setSelectedGuestHouseDetails] = useState(null);
  const [roomsData, setRoomsData] = useState([]);
  const [totalRoomPrice, setTotalRoomPrice] = useState();

  // New state variables for booking submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [bookingResponseData, setBookingResponseData] = useState(null);

  // File upload state variables
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [bookingFieldsEnabled, setBookingFieldsEnabled] = useState(false);

  // Enhanced email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // UPDATED: Helper function to determine if a field should be disabled
  const isFieldDisabled = (fieldName) => {
    if (!editMode) {
      // In create mode, check if it's a name field and selectedGuest exists
      if ((fieldName === 'firstName' || fieldName === 'lastName') && selectedGuest) {
        return true; // Disable name fields when guest is selected from search
      }
      return false; // Other fields are editable in create mode
    }
    
    // In edit mode, only these fields are editable
    const editableFields = ['checkOutDate', 'rooms', 'selectedSuiteRoom', 'finalRoomPrice'];
    return !editableFields.includes(fieldName);
  };

  // Enhanced email change handler
  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    
    if (formSubmitted || emailValue.length > 0) {
      if (!emailValue.trim()) {
        setErrors((prev) => ({
          ...prev,
          email: "Email ID is required",
        }));
      } else if (!validateEmail(emailValue)) {
        setErrors((prev) => ({
          ...prev,
          email: "Please enter a valid email address (e.g., user@example.com)",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
  };

  // Effect to populate form fields when in edit mode
  useEffect(() => {
    if (editMode && bookingData && isOpen && guestHouses.length > 0) {
      populateFormFields(bookingData);
    }
  }, [editMode, bookingData, isOpen, guestHouses]);

  // Function to populate form fields with existing booking data
  const populateFormFields = async (booking) => {
    try {
      // Set guest information
      const nameParts = booking.GuestName?.split(" ") || [""];
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setCpfNo(booking.CPFNo || "");
      setEmail(booking.EmailID || "");
      setMobileNo(booking.MobileNo?.toString() || "");
      setGuestType(booking.GuestType || "");
      
      // Set booking information
      setBookingType(booking.BookingType || "");
      setTravelType(booking.TravelType || "");
      setAdult(booking.NoOfAdults?.toString() || "");
      setChild(booking.NoOfChild?.toString() || "");
      setFinalRoomPrice(booking.FinalRoomRent?.toString() || "");
      
      // Set dates
      if (booking.CheckInDate) {
        setCheckInDate(new Date(booking.CheckInDate));
      }
      if (booking.CheckOutDate) {
        setCheckOutDate(new Date(booking.CheckOutDate));
      }
      
      // Set guest house name
      if (booking.GuestHouseName) {
        setSelectedGuestHouse(booking.GuestHouseName);
      }
      
      // Store room values to be set after guest house details are loaded
      const roomsToSet = booking.NoOfRooms?.toString() || "";
      const suiteRoomsToSet = booking.NoOfSuitesRooms?.toString() || "";
      
      // Set rooms after ensuring guest house details are loaded
      setTimeout(() => {
        if (roomsToSet) {
          setRooms(roomsToSet);
        }
        if (suiteRoomsToSet) {
          setSelectedSuiteRoom(suiteRoomsToSet);
          // Set suite room price
          const suiteRoom = suiteRoomsData.find(suite => suite.id === suiteRoomsToSet);
          if (suiteRoom && suiteRoom.price) {
            setSuiteRoomPrice(suiteRoom.price);
          }
        }
      }, 200);
      
      // Enable booking fields since we have the required data
      setBookingFieldsEnabled(true);
      
      // Mark as existing guest (no need for document upload)
      setSelectedGuest({ GuestName: booking.GuestName });
      
      console.log("Form populated with booking data:", {
        ...booking,
        roomsToSet,
        suiteRoomsToSet
      });
    } catch (error) {
      console.error("Error populating form fields:", error);
    }
  };

  // Create a debounced search function
  const debouncedSearch = debounce(async (query) => {
    if (query.length < 2) return;

    // Save the current focus element
    const activeElement = document.activeElement;

    try {
      setIsSearching(true);
      const response = await bookingService.getAllGuests();
      const filteredGuests = (response.data || []).filter((guest) => {
        const searchTerm = query.toLowerCase();
        const fullName = `${
          guest.guestName || guest.GuestName || ""
        }`.toLowerCase();
        const mobile =
          guest.phoneNo?.toString() || guest.PhoneNo?.toString() || "";
        const email =
          guest.emailId?.toLowerCase() || guest.EmailID?.toLowerCase() || "";

        return (
          fullName.includes(searchTerm) ||
          mobile.includes(searchTerm) ||
          email.includes(searchTerm)
        );
      });

      setSearchResults(filteredGuests);
      setShowSuggestions(true);

      // Restore focus after state update
      setTimeout(() => {
        if (activeElement === searchInputRef.current) {
          searchInputRef.current?.focus();
        }
      }, 0);
    } catch (error) {
      console.error("Error searching guests:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, 300);

  // Handle guest search
  const handleGuestSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length < 2) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    debouncedSearch(query);
  };

  // Handle guest selection
  const handleGuestSelect = async (guestId) => {
    try {
      const guestDetails = await bookingService.getGuestsByGuestId(guestId);
      const guest = guestDetails.data;
      setSelectedGuest(guest);
      setSelectedFile(null);
      setFileError(null);
      setUploadedFileUrl(null);
      setUploadedFileName(null);
      setUploadedFileId(null);
      const nameParts = guest.GuestName?.split(" ") || [""];
      setFirstName(nameParts[0] || "");
      setLastName(nameParts.slice(1).join(" ") || "");
      setCpfNo(guest.CPFNumber || "");
      setEmail(guest.EmailID || "");
      setMobileNo(guest.PhoneNo?.toString() || "");
      setGuestType(guest.GuestType || "");

      setSearchQuery(guest.GuestName || "");
      setShowSuggestions(false);
      setSearchResults([]);
    } catch (error) {
      console.error("Error fetching guest details:", error);
    }
  };

  // Reset guest search
  const resetGuestSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowSuggestions(false);
    setSelectedGuest(null);
  };

  // Clear search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".guest-search-container")) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    console.log("Date/GuestHouse Effect triggered:", {
      selectedGuestHouse,
      checkInDate,
      checkOutDate,
      guestHousesLoaded: guestHouses.length > 0
    });
    
    setBookingFieldsEnabled(
      !!(selectedGuestHouse && checkInDate && checkOutDate)
    );
    
    // Only proceed if guestHouses is loaded and we have all required values
    if (selectedGuestHouse && checkInDate && checkOutDate && guestHouses.length > 0) {
      console.log("All conditions met, calling fetchGuestHouseWithOccupancy");
      fetchGuestHouseWithOccupancy();
    } else {
      console.log("Missing conditions - not calling fetchGuestHouseWithOccupancy");
    }
  }, [selectedGuestHouse, checkInDate, checkOutDate, guestHouses]);

  // Fetch guest house with occupancy
  const fetchGuestHouseWithOccupancy = async () => {
    const selectedGuestHouseObj = guestHouses.find(
      (gh) => gh.guestHouseName === selectedGuestHouse
    );

    console.log("fetchGuestHouseWithOccupancy called with:", {
      selectedGuestHouse,
      checkInDate,
      checkOutDate,
      selectedGuestHouseObj,
      guestHouseId: selectedGuestHouseObj?.id
    });

    // Add safety check
    if (!selectedGuestHouseObj || !selectedGuestHouseObj.id) {
      console.error("No guest house object found or missing ID:", selectedGuestHouseObj);
      return;
    }

    try {
      const formattedCheckIn = formatDateForAPI(checkInDate);
      const formattedCheckOut = formatDateForAPI(checkOutDate);

      console.log("Calling API with:", {
        guestHouseId: selectedGuestHouseObj.id,
        formattedCheckIn,
        formattedCheckOut,
      });

      const response = await guestHouseService.getGuestHouseRoomsDetailById(
        selectedGuestHouseObj.id,
        formattedCheckIn,
        formattedCheckOut
      );

      console.log("API response:", response);

      setSelectedGuestHouseDetails(response);
      updateRoomOptions(response);

      if (response.suites && parseInt(response.suites) > 0) {
        updateSuiteRoomOptions(response);
      }

      // In edit mode, recalculate total price after room details are loaded
      if (editMode && (rooms || selectedSuiteRoom)) {
        setTimeout(() => {
          updateTotalPrice(rooms, selectedSuiteRoom);
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching guest house with occupancy:", error);
    }
  };

  const availableRegularRooms = () => {
    if (selectedGuestHouseDetails) {
      return (
        selectedGuestHouseDetails.totalRooms -
        (selectedGuestHouseDetails.occupiedRegularRooms || 0)
      );
    }
  };

  const updateRoomOptions = (guestHouseDetails) => {
    const roomsOptions = [];
    const totalRooms = guestHouseDetails.totalRooms || 0;
    const occupiedRooms = guestHouseDetails.occupiedRegularRooms || 0;
    const availableRooms = totalRooms - occupiedRooms;

    for (let i = 1; i <= totalRooms; i++) {
      roomsOptions.push({
        id: i,
        name: `${i}`,
        disabled: i > availableRooms && !editMode,
      });
    }

    setRoomsData(roomsOptions);

    // In edit mode, don't reset the rooms selection if it's currently occupied
    if (!editMode && rooms && parseInt(rooms) > availableRooms) {
      setRooms("");
    }
  };

  const updateSuiteRoomOptions = (guestHouseDetails) => {
    const suiteOptions = [];
    const totalSuites = parseInt(guestHouseDetails.suites) || 0;
    const occupiedSuites = guestHouseDetails.occupiedSuiteRooms || 0;
    const availableSuites = totalSuites - occupiedSuites;

    const suitePrice = guestHouseDetails.suites_room_price
      ? guestHouseDetails.suites_room_price.toString()
      : "0";

    for (let i = 1; i <= Math.min(totalSuites, 3); i++) {
      suiteOptions.push({
        id: i.toString(),
        name: `${i}`,
        price: suitePrice,
        disabled: i > availableSuites && !editMode,
      });
    }

    setHasSuiteRooms(totalSuites > 0);
    setSuiteRoomsData(suiteOptions);

    // In edit mode, don't reset the suite room selection if it's currently occupied
    if (!editMode && selectedSuiteRoom && parseInt(selectedSuiteRoom) > availableSuites) {
      setSelectedSuiteRoom("");
      setSuiteRoomPrice("0");
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchGuestHouses();
      setBookingSuccess(false);
      setBookingError(null);
      setBookingResponseData(null);
    }
  }, [isOpen]);

  const fetchGuestHouses = async () => {
    try {
      setIsLoading(true);
      const response = await guestHouseService.getGuestHouseList(false);
      setGuestHouses(response || []);
    } catch (error) {
      console.error("Error fetching guest houses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetAllFields = () => {
    setSelectedGuestHouse("");
    resetGuestSearch();
    setFirstName("");
    setLastName("");
    setCpfNo("");
    setEmail("");
    setMobileNo("");
    setCheckInDate(null);
    setCheckOutDate(null);
    setRooms("");
    setAdult("");
    setChild("");
    setGuestType("");
    setOtherGuestType("");
    setBookingType("");
    setTravelType("");
    setTotalRoomPrice("0");
    setFinalRoomPrice("0");
    setSelectedSuiteRoom("");
    setSuiteRoomPrice("0");
    setErrors({});
  };

  const resetBookingsFields = () => {
    setCheckInDate(null);
    setCheckOutDate(null);
    setRooms("");
    setAdult("");
    setChild("");
    setGuestType("");
    setOtherGuestType("");
    setBookingType("");
    setTravelType("");
    setTotalRoomPrice("0");
    setFinalRoomPrice("0");
    setSelectedSuiteRoom("");
    setSuiteRoomPrice("0");
    setErrors({});
  };

  const fetchGuestHouseDetails = async (id) => {
    try {
      setIsLoading(true);
      const response = await guestHouseService.getGuestHouseRoomsDetailById(id);

      setSelectedGuestHouseDetails(response);
      updateRoomOptions(response);

      if (response.room_price) {
        const baseRoomPrice = response.room_price.toString();
      } else {
        setTotalRoomPrice("0");
        setFinalRoomPrice("0");
      }

      if (response.suites && parseInt(response.suites) > 0) {
        updateSuiteRoomOptions(response);
      } else {
        setHasSuiteRooms(false);
        setSuiteRoomsData([]);
        setSelectedSuiteRoom("");
        setSuiteRoomPrice("0");
      }
    } catch (error) {
      console.error("Error fetching guest house details:", error);
      setHasSuiteRooms(false);
      setSuiteRoomsData([]);
      setSelectedSuiteRoom("");
      setSuiteRoomPrice("0");
      setTotalRoomPrice("0");
      setFinalRoomPrice("0");
    } finally {
      setIsLoading(false);
    }
  };

  const getMinCheckoutDate = () => {
    if (!checkInDate) return new Date();
    const minDate = new Date(checkInDate);
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  };

  const handleCheckInChange = (date) => {
    setCheckInDate(date);
    setErrors((prev) => ({ ...prev, checkInDate: "" }));
  
    if (checkOutDate) {
      const minCheckoutDate = new Date(date);
      minCheckoutDate.setDate(minCheckoutDate.getDate() + 1);
      
      if (checkOutDate < minCheckoutDate) {
        setCheckOutDate(null);
      }
    }
  };

  const handleCheckOutChange = (date) => {
    const minCheckoutDate = getMinCheckoutDate();
    
    if (date < minCheckoutDate) {
      setErrors((prev) => ({
        ...prev,
        checkOutDate: "Check-out date must be at least one day after check-in date",
      }));
      return;
    }

    setCheckOutDate(date);
    setErrors((prev) => ({ ...prev, checkOutDate: "" }));
  };

  const handleGuestHouseChange = (e) => {
    console.log("Guest House selection changed:", e);
    const selectedValue = e.target.value;
    setSelectedGuestHouse(selectedValue);

    // Don't reset fields in edit mode
    if (!editMode) {
      resetBookingsFields();
    }

    if (formSubmitted) {
      setErrors((prev) => ({
        ...prev,
        guestHouse: selectedValue ? "" : "Guest House selection is required",
      }));
    }

    const selected = guestHouses.find(
      (gh) => gh.guestHouseName === selectedValue
    );

    if (selected) {
      fetchGuestHouseDetails(selected.id);
    } else {
      setSelectedGuestHouseDetails(null);
      setRoomsData([]);
      setHasSuiteRooms(false);
      setSuiteRoomsData([]);
      setTotalRoomPrice("0");
      setFinalRoomPrice("0");
    }
  };

  const handleRoomsChange = (e) => {
    const selectedRooms = e.target.value;
    setRooms(selectedRooms);

    if (formSubmitted) {
      setErrors((prev) => ({
        ...prev,
        rooms: selectedRooms ? "" : "Room selection is required",
      }));
    }

    updateTotalPrice(selectedRooms, selectedSuiteRoom);
  };

  const handleSuiteRoomChange = (e) => {
    const selectedSuite = e.target.value;
    setSelectedSuiteRoom(selectedSuite);

    const suiteRoom = suiteRoomsData.find(
      (suite) => suite.id === selectedSuite
    );

    if (suiteRoom && suiteRoom.price) {
      setSuiteRoomPrice(suiteRoom.price);
    } else {
      setSuiteRoomPrice("0");
    }

    updateTotalPrice(rooms, selectedSuite);
  };

  const updateTotalPrice = (selectedRooms, selectedSuite) => {
    let totalPrice = 0;

    if (selectedRooms && selectedGuestHouseDetails?.room_price) {
      const basePrice = selectedGuestHouseDetails.room_price;
      totalPrice += basePrice * parseInt(selectedRooms);
    }

    if (selectedSuite) {
      const suiteRoom = suiteRoomsData.find(
        (suite) => suite.id === selectedSuite
      );
      if (suiteRoom && suiteRoom.price) {
        totalPrice += parseInt(suiteRoom.price) * suiteRoom.id;
      }
    }

    setTotalRoomPrice(totalPrice.toString());
    setFinalRoomPrice(totalPrice.toString());
  };

  const validateForm = () => {
    const newErrors = {};

    if (!firstName.trim()) newErrors.firstName = "First Name is required";
    if (!mobileNo.trim()) newErrors.mobileNo = "Mobile No. is required";
    
    // Enhanced email validation
    if (!email.trim()) {
      newErrors.email = "Email ID is required";
    } else if (!validateEmail(email.trim())) {
      newErrors.email = "Please enter a valid email address (e.g., user@example.com)";
    }
    
    if (!selectedGuestHouse)
      newErrors.guestHouse = "Guest House selection is required";
    if (!checkInDate) newErrors.checkInDate = "Check-in date is required";
    if (!checkOutDate) newErrors.checkOutDate = "Check-out date is required";
    if (!rooms) newErrors.rooms = "Room selection is required";
    if (!adult) newErrors.adult = "Person selection is required";
    if (!guestType) newErrors.guestType = "Guest Type is required";
    if (guestType === "Other" && !otherGuestType.trim())
      newErrors.otherGuestType = "Please specify the guest type";
    if (!bookingType) newErrors.bookingType = "Booking Type is required";
    if (!travelType) newErrors.travelType = "Travel Type is required";

    // Only require document for new guests (not in edit mode)
    if (!editMode && !selectedGuest && !selectedFile) {
      newErrors.document = "Document upload is required for new guests";
    }

    if (mobileNo && !/^\d{10}$/.test(mobileNo.trim())) {
      newErrors.mobileNo = "Please enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const handleSuccessClose = () => {
    setBookingSuccess(false);
    resetForm();
    onClose();
  };

  const closeModal = () => {
    onClose();
    resetForm();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError(null);

    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        setFileError(
          "Invalid file type. Only PDF, JPEG, JPG, and PNG are allowed."
        );
        setSelectedFile(null);
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        setFileError("File size exceeds 5MB limit.");
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      if (formSubmitted && !selectedGuest) {
        setErrors((prev) => ({ ...prev, document: "" }));
      }
    }
  };

  const handleUploadClick = () => {
    document.getElementById("fileInput").click();
  };

  const getFileExtension = (filename) => {
    return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
  };

  const createRenamedFile = (file, bookingId) => {
    const fileExtension = getFileExtension(file.name);
    const newFileName = `${bookingId}.${fileExtension}`;

    const renamedFile = new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return renamedFile;
  };

  const getFileDisplayName = () => {
    if (!selectedFile) return null;
    if (bookingSuccess && bookingResponseData?.data?.bookingId) {
      const fileExtension = getFileExtension(selectedFile.name);
      return `${bookingResponseData.data.bookingId}.${fileExtension}`;
    }
    return selectedFile.name;
  };

 // Add this function to check email existence
const checkEmailExists = async (emailToCheck) => {
  try {
    setIsCheckingEmail(true);
    const response = await authService.checkEmail(emailToCheck);
    return response.exists;
  } catch (error) {
    console.error("Error checking email:", error);
    // If there's an error checking email, allow the form to proceed
    return false;
  } finally {
    setIsCheckingEmail(false);
  }
};

// Modify your handleSubmit function
const handleSubmit = async (e) => {
  e.preventDefault();
  setFormSubmitted(true);

  if (validateForm()) {
    try {
      // Check if email already exists (only for new bookings, not edits)
      if (!editMode && !selectedGuest) {
        const emailExists = await checkEmailExists(email.trim());
        
        if (emailExists) {
          setErrors((prev) => ({
            ...prev,
            email: "This email address is already registered. Please use a different email or search for the existing guest.",
          }));
          return; // Stop form submission
        }
      }

      setIsSubmitting(true);
      setBookingError(null);
      setUploadedFileUrl(null);
      setUploadedFileName(null);

      const selectedGuestHouseObj = guestHouses.find(
        (gh) => gh.guestHouseName === selectedGuestHouse
      );

      const formatDateTimeForAPI = (date) => {
        if (!date) return "";

        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}T12:00:00`;
      };

      const bookingPayload = {
        guestName: firstName + (lastName ? " " + lastName : ""),
        cpfNumber: cpfNo || "",
        emailId: email || "",
        phoneNo: mobileNo,
        guestType: guestType === "Other" ? otherGuestType : guestType,
        roomId: null,
        travelType: travelType,
        bookingType: bookingType,
        checkInDateTime: formatDateTimeForAPI(checkInDate),
        checkOutDateTime: formatDateTimeForAPI(checkOutDate),
        roomType: "",
        roomNumber: "",
        roomRent: finalRoomPrice || "0",
        finalRoomRent: finalRoomPrice || "0",
        guesthouseId: selectedGuestHouseObj?.id || "",
        noOfRooms: rooms || "0",
        noOfSuitesRooms: selectedSuiteRoom || "0",
        noOfAdults: adult || "0",
        noOfChild: child || "0"
      };

      let bookingResponse;

      if (editMode && bookingData) {
        // UPDATE existing booking
        bookingResponse = await bookingService.updateBooking(
          bookingData.BookingID,
          bookingPayload
        );
        console.log("Booking updated successfully:", bookingResponse);
      } else {
        // CREATE new booking
        bookingResponse = await bookingService.createNewBooking(bookingPayload);
        console.log("Booking created successfully:", bookingResponse);
      }

      setBookingSuccess(true);

      // Handle file upload for new bookings
      if (!editMode && bookingResponse?.data?.bookingId && selectedFile) {
        const bookingId = bookingResponse.data.bookingId;
        const renamedFile = createRenamedFile(selectedFile, bookingId);

        const formData = new FormData();
        formData.append("file", renamedFile);
        formData.append("bookingId", bookingId);
        formData.append("guestName", bookingPayload.guestName);
        formData.append("emailId", bookingPayload.emailId);
        formData.append("fileType", renamedFile.type);
        formData.append("description", `Document for booking ${bookingId}`);

        try {
          const fileResponse = await fileService.uploadFile(formData);
          console.log("File uploaded successfully:", fileResponse);

          if (fileResponse?.filePath) {
            setUploadedFileUrl(fileResponse.filePath);
            setUploadedFileId(fileResponse.fileId);
            setUploadedFileName(renamedFile.name);
          }
        } catch (fileError) {
          console.error("File upload failed:", fileError);
          setFileError(`File upload failed: ${fileError.message}`);
        }
      }

      setBookingResponseData({
        ...bookingResponse,
        guestHouseName: selectedGuestHouse,
        checkInDate: formatDateForAPI(checkInDate),
        checkOutDate: formatDateForAPI(checkOutDate),
      });
    } catch (error) {
      setBookingError(
        error.message || `Failed to ${editMode ? 'update' : 'create'} booking. Please try again.`
      );
      console.error(`Error ${editMode ? 'updating' : 'creating'} booking:`, error);
    } finally {
      setIsSubmitting(false);
    }
  } else {
    console.log("Form has errors, please correct them");
  }
};
  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setCpfNo("");
    setEmail("");
    setMobileNo("");
    setSelectedGuestHouse("");
    resetAllFields();
    setFormSubmitted(false);
    setBookingSuccess(false);
    setBookingError(null);
    setSelectedFile(null);
    setFileError(null);
  };

  if (!isOpen) return null;

  if (bookingSuccess && bookingResponseData) {
    return (
      <BookingSuccessModal
        isOpen={bookingSuccess}
        onClose={handleSuccessClose}
        bookingData={bookingResponseData}
        guestName={firstName + (lastName ? " " + lastName : "")}
        guestHouseName={selectedGuestHouse}
        checkInDate={formatDateForAPI(checkInDate)}
        checkOutDate={formatDateForAPI(checkOutDate)}
        roomCount={rooms}
        suiteCount={selectedSuiteRoom || "0"}
        totalPrice={finalRoomPrice}
        editMode={editMode}
      />
    );
  }
  
  console.log("Rendering BookingComponent with editMode:", editMode); 
  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{editMode ? "Edit Room Booking" : "Room Booking"}</h2>
          <button className="close-btn" onClick={closeModal}>
            ×
          </button>
        </div>

        {bookingError && (
          <div className="booking-error-message">{bookingError}</div>
        )}

        <div className="booking-modal-body">
          <form className="booking-form" onSubmit={handleSubmit}>
            <h3>Guest Information</h3>

            {/* Hide guest search in edit mode */}
            {!editMode && (
              <div className="form-row">
                <div className="form-group guest-search-container">
                  <label>Search Guest</label>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      ref={searchInputRef}
                      placeholder="Search by name, mobile, or email"
                      value={searchQuery}
                      onChange={handleGuestSearch}
                      onFocus={() => {
                        if (searchResults.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                      autoComplete="off"
                      disabled={isSearching}
                    />
                    {isSearching && (
                      <div className="search-loading">Searching...</div>
                    )}

                    {showSuggestions && searchResults.length > 0 && (
                      <div className="suggestions-dropdown">
                        {searchResults.map((guest) => (
                          <div
                            key={guest.GuestID}
                            className="suggestion-item"
                            onClick={() => handleGuestSelect(guest.GuestID)}
                          >
                            <div className="guest-info">
                              <div className="guest-name">
                                {guest.GuestName}{" "}
                                {guest.PhoneNo && <span> {guest.PhoneNo}</span>}{" "}
                                {guest.EmailId && <span> {guest.EmailId}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {showSuggestions &&
                      searchQuery.length >= 2 &&
                      searchResults.length === 0 &&
                      !isSearching && (
                        <div className="suggestions-dropdown">
                          <div className="no-results">No guests found</div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  placeholder="Enter Guest Name"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (formSubmitted) {
                      setErrors((prev) => ({
                        ...prev,
                        firstName: e.target.value.trim()
                          ? ""
                          : "First Name is required",
                      }));
                    }
                  }}
                  className={`${errors.firstName ? "error-input" : ""} ${
                    selectedGuest && !editMode ? "readonly-field" : ""
                  }`}
                  disabled={isFieldDisabled('firstName')}
                  readOnly={selectedGuest && !editMode}
                />
                {errors.firstName && (
                  <div className="error-message">{errors.firstName}</div>
                )}
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Enter Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={selectedGuest && !editMode ? "readonly-field" : ""}
                  disabled={isFieldDisabled('lastName')}
                  readOnly={selectedGuest && !editMode}
                />
              </div>
              <div className="form-group">
                <label>CPF No.</label>
                <input
                  type="text"
                  placeholder="Enter CPF No."
                  value={cpfNo}
                  onChange={(e) => setCpfNo(e.target.value)}
                  disabled={isFieldDisabled('cpfNo')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email ID *</label>
                <input
                  type="email"
                  placeholder="Enter Email ID (e.g., user@example.com)"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={() => {
                    // Validate on blur as well for better UX
                    if (email && !validateEmail(email)) {
                      setErrors((prev) => ({
                        ...prev,
                        email: "Please enter a valid email address (e.g., user@example.com)",
                      }));
                    }
                  }}
                  className={errors.email ? "error-input" : ""}
                  disabled={isFieldDisabled('email')}
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>
              <div className="form-group">
                <label>Mobile No.*</label>
                <input
                  type="text"
                  placeholder="Enter Mobile No."
                  value={mobileNo}
                  maxLength={10}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setMobileNo(numericValue);

                    if (formSubmitted) {
                      if (!numericValue.trim()) {
                        setErrors((prev) => ({
                          ...prev,
                          mobileNo: "Mobile No. is required",
                        }));
                      } else if (numericValue.length !== 10) {
                        setErrors((prev) => ({
                          ...prev,
                          mobileNo:
                            "Please enter a valid 10-digit mobile number",
                        }));
                      } else {
                        setErrors((prev) => ({ ...prev, mobileNo: "" }));
                      }
                    }
                  }}
                  className={errors.mobileNo ? "error-input" : ""}
                  disabled={isFieldDisabled('mobileNo')}
                />
                {errors.mobileNo && (
                  <div className="error-message">{errors.mobileNo}</div>
                )}
              </div>
            </div>

            <h3>Booking Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Guest House*</label>
                <select
                  disabled={isLoading || isFieldDisabled('selectedGuestHouse')}
                  value={selectedGuestHouse}
                  onChange={handleGuestHouseChange}
                  className={errors.guestHouse ? "error-input" : ""}
                >
                  <option value="">Select Guest House</option>
                  {isLoading ? (
                    <option>Loading guest houses...</option>
                  ) : (
                    guestHouses.map((guestHouse) => (
                      <option
                        key={guestHouse.id}
                        value={guestHouse.guestHouseName}
                      >
                        {guestHouse.guestHouseName}
                      </option>
                    ))
                  )}
                </select>
                {errors.guestHouse && (
                  <div className="error-message">{errors.guestHouse}</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Check In*</label>
                <div className="date-input-wrapper">
                  <DatePicker
                    selected={checkInDate}
                    onChange={handleCheckInChange}
                    placeholderText="Select Check-In Date"
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                    className={`date-picker ${
                      errors.checkInDate ? "error-input" : ""
                    }`}
                    disabled={isFieldDisabled('checkInDate')}
                  />
                  <span className="calendar-icon">📅</span>
                </div>
                {errors.checkInDate && (
                  <div className="error-message">{errors.checkInDate}</div>
                )}
              </div>
              <div className="form-group">
                <label>Check Out*</label>
                <div className="date-input-wrapper">
                  <DatePicker
                    selected={checkOutDate}
                    onChange={handleCheckOutChange}
                    placeholderText="Select Check-Out Date"
                    dateFormat="dd/MM/yyyy"
                    minDate={getMinCheckoutDate()}
                    disabled={!checkInDate || isFieldDisabled('checkOutDate')}
                    className={`date-picker ${
                      errors.checkOutDate ? "error-input" : ""
                    }`}
                  />
                  <span className="calendar-icon">📅</span>
                </div>
                {errors.checkOutDate && (
                  <div className="error-message">{errors.checkOutDate}</div>
                )}
              </div>
              <div className="form-group">
                <label>Rooms*</label>
                <select
                  value={rooms}
                  onChange={handleRoomsChange}
                  className={errors.rooms ? "error-input" : ""}
                  disabled={isFieldDisabled('rooms')}
                >
                  <option value="">Select Rooms</option>
                  {roomsData.length > 0 ? (
                    roomsData.map((room) => (
                      <option
                        key={room.id}
                        value={room.id}
                        disabled={room.disabled}
                      >
                        {room.name}
                      </option>
                    ))
                  ) : (
                    <option disabled>No rooms available</option>
                  )}
                </select>
                {errors.rooms && (
                  <div className="error-message">{errors.rooms}</div>
                )}
              </div>
            </div>

            {hasSuiteRooms && (
              <div className="form-row">
                <div className="form-group">
                  <label>Suites Room</label>
                  <select
                    value={selectedSuiteRoom}
                    onChange={handleSuiteRoomChange}
                    disabled={isFieldDisabled('selectedSuiteRoom')}
                  >
                    <option value="">Select Suite Room</option>
                    {suiteRoomsData.map((suite) => (
                      <option
                        key={suite.id}
                        value={suite.id}
                        disabled={suite.disabled}
                      >
                        {suite.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Person*</label>
                <select
                  value={adult}
                  onChange={(e) => {
                    setAdult(e.target.value);
                    if (formSubmitted) {
                      setErrors((prev) => ({
                        ...prev,
                        adult: e.target.value
                          ? ""
                          : "Person selection is required",
                      }));
                    }
                  }}
                  disabled={isFieldDisabled('adult')}
                  className={errors.adult ? "error-input" : ""}
                >
                  <option value="">Select Person</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                </select>
                {errors.adult && (
                  <div className="error-message">{errors.adult}</div>
                )}
              </div>
              <div className="form-group">
                <label>Child</label>
                <select
                  value={child}
                  disabled={isFieldDisabled('child')}
                  onChange={(e) => setChild(e.target.value)}
                >
                  <option value="">Select Child</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                </select>
              </div>
              <div className="form-group">
                <label>Guest Type *</label>
                <select
                  value={guestType}
                  onChange={(e) => {
                    setGuestType(e.target.value);
                    if (formSubmitted) {
                      setErrors((prev) => ({
                        ...prev,
                        guestType: e.target.value
                          ? ""
                          : "Guest Type is required",
                      }));
                    }
                  }}
                  disabled={isFieldDisabled('guestType')}
                  className={errors.guestType ? "error-input" : ""}
                >
                  <option value="">Select Guest Type</option>
                  <option value="Working Employee">Working Employee</option>
                  <option value="Retired Employee">Retired Employee</option>
                  <option value="Company Guest">Company Guest</option>
                  <option value="Other">Other</option>
                </select>
                {errors.guestType && (
                  <div className="error-message">{errors.guestType}</div>
                )}
              </div>
            </div>

            {guestType === "Other" && (
              <div className="form-row">
                <div className="form-group">
                  <label>Specify Guest Type *</label>
                  <input
                    type="text"
                    placeholder="Please specify guest type"
                    value={otherGuestType}
                    onChange={(e) => {
                      setOtherGuestType(e.target.value);
                      if (formSubmitted) {
                        setErrors((prev) => ({
                          ...prev,
                          otherGuestType: e.target.value.trim()
                            ? ""
                            : "Please specify the guest type",
                        }));
                      }
                    }}
                    disabled={isFieldDisabled('otherGuestType')}
                    className={errors.otherGuestType ? "error-input" : ""}
                  />
                  {errors.otherGuestType && (
                    <div className="error-message">{errors.otherGuestType}</div>
                  )}
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Booking Type*</label>
                <select
                  value={bookingType}
                  onChange={(e) => {
                    setBookingType(e.target.value);
                    if (formSubmitted) {
                      setErrors((prev) => ({
                        ...prev,
                        bookingType: e.target.value
                          ? ""
                          : "Booking Type is required",
                      }));
                    }
                  }}
                  disabled={isFieldDisabled('bookingType')}
                  className={errors.bookingType ? "error-input" : ""}
                >
                  <option value="">Select Booking Type</option>
                  <option value="Self">Self</option>
                  <option value="Relative">Relative</option>
                  <option value="Other">Other</option>
                </select>
                {errors.bookingType && (
                  <div className="error-message">{errors.bookingType}</div>
                )}
              </div>
              <div className="form-group">
                <label>Travel Type*</label>
                <select
                  value={travelType}
                  onChange={(e) => {
                    setTravelType(e.target.value);
                    if (formSubmitted) {
                      setErrors((prev) => ({
                        ...prev,
                        travelType: e.target.value
                          ? ""
                          : "Travel Type is required",
                      }));
                    }
                  }}
                  disabled={isFieldDisabled('travelType')}
                  className={errors.travelType ? "error-input" : ""}
                >
                  <option value="">Select Travel Type</option>
                  <option value="Official">Official</option>
                  <option value="Personal">Personal</option>
                  <option value="Medical">Medical</option>
                </select>
                {errors.travelType && (
                  <div className="error-message">{errors.travelType}</div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Final Room Price</label>
                <input
                  type="text"
                  value={finalRoomPrice}
                  disabled={isFieldDisabled('finalRoomPrice')}
                  onChange={(e) => {
                    setFinalRoomPrice(e.target.value);
                    if (formSubmitted && isNaN(e.target.value)) {
                      setErrors((prev) => ({
                        ...prev,
                        finalRoomPrice: "Please enter a valid number",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, finalRoomPrice: "" }));
                    }
                  }}
                  placeholder="Enter final price"
                  className={errors.finalRoomPrice ? "error-input" : ""}
                />
                {errors.finalRoomPrice && (
                  <div className="error-message">{errors.finalRoomPrice}</div>
                )}
              </div>
              {/* Only show file upload for new bookings */}
              {!editMode && (
                <div className="form-group">
                  <label>Document Upload* (Aadhar, Office ID, Driving License, PAN only)</label>
                  <input
                    type="file"
                    id="fileInput"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    disabled={!bookingFieldsEnabled}
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={handleUploadClick}
                    disabled={!bookingFieldsEnabled}
                  >
                    {selectedFile ? getFileDisplayName() : "Upload"}
                  </button>
                  {fileError && (
                    <div className="error-message">{fileError}</div>
                  )}
                  {errors.document && (
                    <div className="error-message">{errors.document}</div>
                  )}
                  {selectedFile && !fileError && (
                    <div className="file-success">
                      File selected: {getFileDisplayName()}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                style={{ width: "294px" }}
                className="book-now-btn"
                 disabled={isSubmitting || isCheckingEmail}
              >
                {isSubmitting 
                  ? "PLEASE WAIT..." 
                  : editMode 
                    ? "UPDATE BOOKING" 
                    : "BOOK NOW"
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingComponent;
